import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowUpRight } from "lucide-react";
import { api, API, CONTACT_EMAIL } from "@/lib/api";

const HERO_IMG = `${API}/files/yarnthread/hero/kichazone-collection.webp`;
const TEXTURE_IMG = "https://images.unsplash.com/photo-1619608155967-7a5058b2a963?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export const MenuOverlay = ({ open, onClose }) => {
    const navigate = useNavigate();
    const [sections, setSections] = useState([]);

    useEffect(() => {
        api.get("/products")
            .then((r) => setSections([...new Set(r.data.map((p) => p.category))]))
            .catch(() => {});
    }, []);

    const go = (path) => {
        onClose();
        navigate(path);
    };

    const goCraft = () => {
        onClose();
        navigate("/");
        setTimeout(() => document.getElementById("craft")?.scrollIntoView({ behavior: "smooth" }), 500);
    };

    const links = [
        { num: "01", label: "Home", action: () => go("/"), testid: "menu-link-home" },
        { num: "02", label: "Shop", action: () => go("/shop"), testid: "menu-link-shop" },
        { num: "03", label: "Our Craft", action: goCraft, testid: "menu-link-craft" },
        { num: "04", label: "Contact", action: () => { onClose(); window.location.href = `mailto:${CONTACT_EMAIL}`; }, testid: "menu-link-contact" },
        { num: "05", label: "Maker Login", action: () => go("/admin"), testid: "menu-link-admin" },
    ];

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    data-testid="menu-overlay"
                    className="fixed inset-0 z-[70] flex flex-col bg-espresso text-sand"
                    initial={{ clipPath: "inset(0 0 100% 0)" }}
                    animate={{ clipPath: "inset(0 0 0% 0)" }}
                    exit={{ clipPath: "inset(0 0 100% 0)" }}
                    transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                >
                    <div className="flex items-center justify-between border-b border-sand/10 px-6 py-4 lg:px-10">
                        <button onClick={() => go("/")} data-testid="menu-logo-home" className="flex items-center rounded-full bg-white px-4 py-2">
                            <img src="/logo.png" alt="Kichazone — home" className="h-10 w-auto" />
                        </button>
                        <button
                            onClick={onClose}
                            data-testid="menu-close-button"
                            className="flex items-center gap-2 border border-sand/40 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-sand hover:text-espresso"
                        >
                            <X size={14} /> Close
                        </button>
                    </div>
                    <div className="grid flex-1 grid-cols-1 lg:grid-cols-12">
                        <nav className="flex flex-col justify-center px-6 py-10 lg:col-span-7 lg:px-10">
                            {links.map((link, i) => (
                                <span key={link.num} className="block overflow-hidden border-b border-sand/10 last:border-0">
                                    <motion.button
                                        onClick={link.action}
                                        data-testid={link.testid}
                                        initial={{ y: "110%" }}
                                        animate={{ y: "0%" }}
                                        transition={{ duration: 0.8, delay: 0.25 + i * 0.08, ease: [0.76, 0, 0.24, 1] }}
                                        className="group flex w-full items-baseline gap-6 py-4 text-left lg:py-5"
                                    >
                                        <span className="font-mono text-xs text-sand/40 transition-colors duration-300 group-hover:text-gold">{link.num}</span>
                                        <span className="font-serif text-4xl font-light tracking-tight transition-all duration-300 group-hover:translate-x-3 group-hover:italic group-hover:text-gold md:text-6xl">
                                            {link.label}
                                        </span>
                                        <ArrowUpRight size={22} className="ml-auto shrink-0 text-sand/30 transition-all duration-300 group-hover:rotate-45 group-hover:text-gold" />
                                    </motion.button>
                                </span>
                            ))}
                            {sections.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.8, delay: 0.75 }}
                                    className="mt-10"
                                    data-testid="menu-sections"
                                >
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sand/40">Shop by section</p>
                                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                                        {sections.map((s) => (
                                            <button
                                                key={s}
                                                onClick={() => go(`/shop?category=${encodeURIComponent(s)}`)}
                                                data-testid={`menu-section-${s.toLowerCase()}`}
                                                className="font-mono text-xs uppercase tracking-[0.2em] text-sand/70 transition-colors duration-300 hover:text-gold"
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </nav>
                        <div className="relative hidden lg:col-span-5 lg:block">
                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                className="absolute right-10 top-12 w-64"
                            >
                                <div className="absolute -left-3 -top-3 h-full w-full border border-sand/30" aria-hidden="true" />
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img src={HERO_IMG} alt="Crochet tote" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" data-testid="menu-image-hero" />
                                </div>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.9, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
                                className="absolute bottom-16 right-56 w-48"
                            >
                                <div className="relative aspect-square overflow-hidden border border-sand/30">
                                    <img src={TEXTURE_IMG} alt="Colorful yarn" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" data-testid="menu-image-texture" />
                                </div>
                                <span className="absolute -bottom-6 left-0 font-mono text-[10px] uppercase tracking-[0.25em] text-sand/40">Est. 2026</span>
                            </motion.div>
                        </div>
                    </div>
                    <div className="flex items-center justify-between border-t border-sand/10 px-6 py-5 font-mono text-[10px] uppercase tracking-[0.25em] text-sand/40 lg:px-10">
                        <a href={`mailto:${CONTACT_EMAIL}`} data-testid="menu-email-link" className="transition-colors hover:text-ochre">{CONTACT_EMAIL}</a>
                        <span className="hidden sm:inline">Made slowly, by hand</span>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
